import styled from "@emotion/styled";
import { NATIVE_SOL } from "@stepfinance/common";
import type { StepColumnsType } from "@stepfinance/react";
import {
  darkGrey2,
  darkGrey4,
  getColumnSearchProps,
  lightGreen,
  PLUS_JAKARTA_SANS,
  SPACE_MONO,
} from "@stepfinance/react";
import { StepTable } from "@stepfinance/react";
import {
  CheckboxFilter,
  ContextButton,
  formatBalance,
  IconTextContainer,
  makeSimpleFilters,
  red,
  transparentWhite,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import { Col, Row } from "antd";
import type { FilterValue, SorterResult } from "antd/lib/table/interface";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import shallow from "zustand/shallow";

import { ReactComponent as DownloadIcon } from "../../assets/download.svg";
import { ReactComponent as Lock } from "../../assets/lock.svg";
import { ReactComponent as Minus } from "../../assets/minusIcon.svg";
import { ReactComponent as Plus } from "../../assets/plusIcon.svg";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import type { DownloadHistoryMutationParams } from "../../hooks/queries/transactionHistory/useDownloadHistory";
import { useDownloadHistory } from "../../hooks/queries/transactionHistory/useDownloadHistory";
import type { LockedTransactions, UserTransaction } from "../../hooks/queries/transactionHistory/useTransactionHistory";
import { useTransactionHistoryQuery } from "../../hooks/queries/transactionHistory/useTransactionHistory";
import type { TransactionsFilters } from "../../hooks/stores/transactionHistoryStore";
import {
  defaultEndDate,
  defaultStartDate,
  useTransactionHistoryStore,
} from "../../hooks/stores/transactionHistoryStore";
import { notify } from "../../utils/notifications";
import { getTokenByMint } from "../../utils/tokenInfo";
import { isUserTransactionLockedTxs, walletIsFeePayer } from "../../utils/transaction-history/utils";
import { formatStringWithPlaceholders } from "../../utils/utils";
import { NavigationLink } from "../shared";
import { StepDatePicker } from "../StepDatePicker/StepDatePicker";
import { TokenIcon } from "../TokenIcon";
import { PlatformCell } from "../tools/OpenOrders/PlatformCell";
import type { TransactionHistoryIconTypes } from "./Icons";
import { getTransactionHistoryIconComponent } from "./Icons";

//MOVE to COMMON
export const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  year: "numeric",
});

interface UserTransactionsProps {
  showDownloadButton?: boolean;
  showDateFilter?: boolean;
  /** Transactions will be shown only for the specified year */
  transactionsYear?: number | string;
  hasUserATierForCurrentYear?: boolean;
  platforms: string[];
  isPageLoading?: boolean;
  totalTransactionsMade: number;
  lastTransactionUnlockedDate?: string;
}

export const UserTransactions: FC<UserTransactionsProps> = ({
  showDownloadButton,
  showDateFilter,
  transactionsYear,
  hasUserATierForCurrentYear,
  platforms,
  isPageLoading,
  totalTransactionsMade,
  lastTransactionUnlockedDate,
}) => {
  const { publicKey } = useEnrichedWallet();
  const downloadHistoryMutation = useDownloadHistory(publicKey?.toBase58());
  const {
    data: transactionsData,
    isError,
    fetchNextPage,
    isFetching,
    isLoading: isLoadingMoreItems,
    isInitialLoading,
    hasNextPage,
  } = useTransactionHistoryQuery(publicKey?.toBase58(), transactionsYear, hasUserATierForCurrentYear);
  const [filters, updateFilters, setTiersModalOpen] = useTransactionHistoryStore(
    (state) => [state.filters, state.updateFilters, state.setTiersModalOpen],
    shallow,
  );
  const [startDate, setStartDate] = useState<Date | undefined>(
    filters.startDate ? new Date(filters.startDate) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(filters.endDate ? new Date(filters.endDate) : undefined);
  const [hasAllTransactionsLoaded, setHasAllTransactionsLoaded] = useState(false);
  const {
    currentLanguageData: {
      pages: { transactionHistory },
    },
    // languageSetting,
  } = useLanguageContext();

  const applyFilters = useCallback(
    async (newFilters?: TransactionsFilters) => {
      const newerFilters = {
        ...newFilters,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      };
      updateFilters(newerFilters);
      setHasAllTransactionsLoaded(false);
    },
    [endDate, startDate, updateFilters],
  );

  useEffect(() => {
    if (!hasNextPage && hasNextPage !== undefined && !isError) {
      setHasAllTransactionsLoaded(true);
    }
  }, [hasNextPage, isError, isInitialLoading, isPageLoading]);

  const onReachScrollEnd = useCallback(async () => {
    const pages = transactionsData?.pages;
    // NOTE: SOLUTION IS NOT OPTIMAL SINCE IT SHOULD BE DONE AT THE QUERY LEVEL, NOT THE COMPONENT. A.K.A. should come from
    // the "hasNextPage" variable from the query
    const itemsPerPage = filters.itemsPerPage ?? 35;
    const isLastPage = pages && pages.length > 0 && pages[pages.length - 1].length < itemsPerPage;
    if (hasNextPage && !isLastPage) {
      await fetchNextPage();
    } else {
      setHasAllTransactionsLoaded(true);
    }
  }, [fetchNextPage, filters.itemsPerPage, hasNextPage, transactionsData?.pages]);

  const onStartDateChange = useCallback(
    (date: Date | null) => {
      const unlockedDate = lastTransactionUnlockedDate ? new Date(lastTransactionUnlockedDate) : undefined;
      if (unlockedDate && date && date > unlockedDate) {
        notify({
          type: "error",
          message: transactionHistory.calendar.errorSelectingDateFilters,
          duration: 5,
        });
        return;
      }
      if (date) {
        date.setHours(0, 0, 0, 0);
        setStartDate(date);
      } else {
        setStartDate;
      }
    },
    [lastTransactionUnlockedDate, transactionHistory.calendar.errorSelectingDateFilters],
  );

  const onEndDateChange = useCallback((date: Date | null) => {
    if (date) {
      date.setHours(23, 59, 59, 999);
      setEndDate(date);
    } else {
      setEndDate(undefined);
    }
  }, []);

  const resetDateFilters = useCallback(() => {
    setStartDate(defaultStartDate());
    setEndDate(defaultEndDate());
  }, []);

  useEffect(() => {
    // when filters change we check to match the date pickers with the filters
    // note that date pickers can change independently of the filters before Apply is clicked
    const filterStartDate = filters.startDate ? new Date(filters.startDate) : undefined;
    const filtersEndDate = filters.endDate ? new Date(filters.endDate) : undefined;
    if (filterStartDate && filterStartDate !== startDate) {
      setStartDate(filterStartDate);
    }
    if (filtersEndDate && filtersEndDate !== endDate) {
      setEndDate(filtersEndDate);
    }

    // only update if the filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.endDate, filters.startDate]);

  const onDownloadHistory = async () => {
    const data: DownloadHistoryMutationParams = {
      filters: filters,
      year: transactionsYear,
    };
    try {
      await downloadHistoryMutation.mutateAsync(data);
    } catch (_) {}
  };

  const userTxTableData = useMemo(() => {
    return hasUserATierForCurrentYear
      ? transactionsData?.pages?.flat()
      : ([{ remainingTxCount: totalTransactionsMade }] as UserTransaction[]);
  }, [hasUserATierForCurrentYear, totalTransactionsMade, transactionsData?.pages]);

  const columns: StepColumnsType<UserTransaction> = [
    {
      className: "column-custom",
      shouldDisplayRow: (record) => isUserTransactionLockedTxs(record) && record.remainingTxCount > 0,
      onRender: (_, record) => {
        return (
          <LockedTransactionsContainer>
            <Lock color={red} />
            <p className="locked-txs-title">
              {formatStringWithPlaceholders(
                transactionHistory.lockedTransactionsAmount,
                (record as LockedTransactions).remainingTxCount,
                transactionsYear,
              )}
            </p>
            <ContextButton onClick={() => setTiersModalOpen(true)}>{transactionHistory.unlockButton}</ContextButton>
          </LockedTransactionsContainer>
        );
      },
    },
    {
      title: "Date",
      key: "txDateTime",
      width: 190,
      className: "column-string",
      onRender: (_, record) => {
        if (isUserTransactionLockedTxs(record)) {
          return null;
        }
        return <>{DATE_FORMATTER.format(new Date(record.txDateTime))}</>;
      },
      sorter: (_a, _b) => {
        return 1;
      },
    },
    {
      title: "TXID",
      key: "txId",
      width: 80,
      onRender: (_, record) => {
        if (isUserTransactionLockedTxs(record)) {
          return null;
        }
        return (
          <NavigationLink href={`https://solscan.io/tx/${record.txSignature}`} target="_blank">
            <ShortSpan>{record.txSignature}</ShortSpan>
          </NavigationLink>
        );
      },
      ...getColumnSearchProps((record) => {
        if (isUserTransactionLockedTxs(record)) {
          return "";
        }
        return record.txSignature;
      }, "transaction id"),
    },
    {
      title: "Platforms",
      key: "platform",
      width: 250,
      onRender: (_, record) => {
        if (isUserTransactionLockedTxs(record)) {
          return null;
        }
        if (!record.metadata.platform.length) {
          return <ZeroStateSpan>{transactionHistory.transactionTypes.unknown}</ZeroStateSpan>;
        }
        return (
          <>
            {record.metadata.platform.map((ele, idx) => (
              <PlatformCell platform={ele} key={idx} />
            ))}
          </>
        );
      },
      filters: makeSimpleFilters(platforms),
      filterDropdown: (props) => {
        return <CheckboxFilter {...props} />;
      },
    },
    {
      title: "Type",
      key: "action",
      width: 150,
      className: "column-string",
      onRender: (_, record) => {
        if (isUserTransactionLockedTxs(record)) {
          return null;
        }

        if (!record.metadata.action.length) {
          return <ZeroStateSpan>{transactionHistory.transactionTypes.unknown}</ZeroStateSpan>;
        }

        return (
          <>
            {record.metadata.action.map((ele, indx) => {
              const IconComponent: FC = getTransactionHistoryIconComponent(ele as TransactionHistoryIconTypes);
              return (
                <IconComponentContainer key={indx}>
                  <IconComponent />
                </IconComponentContainer>
              );
            })}
          </>
        );
      },
    },
    {
      title: "Outgoing",
      key: "outgoing",
      onRender: (_, record) => {
        if (isUserTransactionLockedTxs(record)) {
          return null;
        }
        const sentData = Object.entries(record.metadata.parsedBalanceChanges.sent);

        if (walletIsFeePayer(record)) {
          // Add paid tx fee as a separate row
          sentData.push([NATIVE_SOL.toBase58(), record.txFee]);
        }

        if (!sentData.length) {
          return <ZeroStateSpan>--</ZeroStateSpan>;
        }

        return (
          <>
            {sentData.map(([mint, value], indx) => {
              const token = getTokenByMint(mint);
              const symbolOrName = token?.symbol && token?.symbol.length > 0 ? token?.symbol : token?.name;
              const description = symbolOrName ? (
                <MediumSpan> {symbolOrName} </MediumSpan>
              ) : (
                <ShortSpan>{mint}</ShortSpan>
              );
              return (
                <TokenWrapper key={indx} indx={indx}>
                  <Minus />
                  <TokenIcon mintAddress={mint} />
                  <span>{formatBalance(value, token?.decimals)}</span>
                  <NavigationLink
                    href={`https://analytics.step.finance/defionsolana/token?mint=${mint}`}
                    target="_blank"
                  >
                    {description}
                  </NavigationLink>
                </TokenWrapper>
              );
            })}
          </>
        );
      },
    },
    {
      title: "Incoming",
      key: "incoming",
      className: "column-string",
      onRender: (_, record) => {
        if (isUserTransactionLockedTxs(record)) {
          return null;
        }
        const receivedData = Object.entries(record.metadata.parsedBalanceChanges.recieved);
        if (!receivedData.length) {
          return <ZeroStateSpan>--</ZeroStateSpan>;
        }
        return (
          <>
            {receivedData.map(([mint, value], indx) => {
              const token = getTokenByMint(mint);
              const symbolOrName = token?.symbol && token?.symbol.length > 0 ? token?.symbol : token?.name;
              const description = symbolOrName ? (
                <MediumSpan> {symbolOrName} </MediumSpan>
              ) : (
                <ShortSpan>{mint}</ShortSpan>
              );
              return (
                <TokenWrapper key={indx} indx={indx}>
                  <Plus />
                  <TokenIcon mintAddress={mint} />
                  <span>{formatBalance(value, token?.decimals)}</span>
                  <NavigationLink
                    href={`https://analytics.step.finance/defionsolana/token?mint=${mint}`}
                    target="_blank"
                  >
                    {description}
                  </NavigationLink>
                </TokenWrapper>
              );
            })}
          </>
        );
      },
    },
  ];

  const disabledDates = (current: Date) => {
    const isDateWithinSelectedYear = current.getFullYear() === Number(transactionsYear);

    return !!transactionsYear && !isDateWithinSelectedYear;
  };

  const handleTableChange = async (
    _: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    // We don't use multiple sorting
    if (!Array.isArray(sorter)) {
      await applyFilters({
        sortDesc: !(sorter.order === "ascend"),
        // TODO: Add validation in the frontend to be sure that the id given is a tx id(this way we can avoid sending
        // an unnecessary request to the backend due to the tx id not existing)
        txId: tableFilters.txId ? (tableFilters.txId[0] as string) : undefined,
        platforms: tableFilters.platform ? (tableFilters.platform as string[]) : undefined,
      });
    }
  };

  return (
    <>
      <HistorySettingsContainer>
        {showDownloadButton && (
          <div className="download-history-container">
            <ContextButton
              className="download-history-button"
              icon={<DownloadIcon width={20} height={20} />}
              onClick={onDownloadHistory}
              disabled={!hasUserATierForCurrentYear}
            >
              {transactionHistory.downloadHistoryButton}
            </ContextButton>
          </div>
        )}
        {showDateFilter && (
          <Row gutter={[10, 16]} justify="space-between" className="inputs-container">
            <Col xl={18} xs={24}>
              <StepDatePicker
                className="date-picker"
                size="large"
                inputs="double"
                placeholder={transactionHistory.pickStartDate}
                endDatePlaceholder={transactionHistory.pickEndDate}
                placement="bottomRight"
                allowClear
                format={"YYYY/MM/DD"}
                date={startDate}
                endDate={endDate}
                showToday={false}
                disabled={!hasUserATierForCurrentYear}
                disabledStartDate={disabledDates}
                disabledEndDate={disabledDates}
                onDateChange={onStartDateChange}
                onEndDateChange={onEndDateChange}
              />
            </Col>
            <Col xl={6} xs={24}>
              <div className="form-button-container">
                <ContextButton
                  className="form-button"
                  onClick={() => applyFilters()}
                  disabled={!hasUserATierForCurrentYear}
                >
                  {transactionHistory.applyButton}
                </ContextButton>
                <ContextButton
                  onClick={resetDateFilters}
                  disabled={!hasUserATierForCurrentYear}
                  className="form-button"
                >
                  {transactionHistory.resetFilters}
                </ContextButton>
              </div>
            </Col>
          </Row>
        )}
      </HistorySettingsContainer>
      <StepTable<UserTransaction>
        rowKey={(record) => {
          if (isUserTransactionLockedTxs(record)) {
            return "locked-txs";
          }
          return record.txSignature;
        }}
        columns={columns}
        id="transactions-table"
        className="transactions-table"
        dataSource={userTxTableData}
        loading={isInitialLoading || (isFetching && userTxTableData?.length === 0) || isPageLoading}
        onReachScrollEnd={onReachScrollEnd}
        onChange={handleTableChange}
        hasFinishedLoadingAllItems={hasAllTransactionsLoaded || !hasUserATierForCurrentYear}
        infiniteScroll
        isLoadingMoreItems={isLoadingMoreItems}
        scroll={{ y: 600, x: 190 + 80 + 200 + 150 + 500 }}
      />
    </>
  );
};

const HistorySettingsContainer = styled.div`
  margin-bottom: 16px;
  .download-history-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
    .download-history-button {
      margin-right: 10px;
      height: 36px;
    }
  }
  .inputs-container {
    .date-picker {
      width: 100%;
      height: 36px;
      font-family: ${SPACE_MONO};
      input {
        &::placeholder {
          font-family: ${PLUS_JAKARTA_SANS};
        }
      }
      &.date-picker:hover,
      &.ant-picker-focused {
        border-color: ${lightGreen};
      }
      border: 1px solid ${darkGrey4};
      background-color: ${darkGrey2};
      color: ${transparentWhite};
    }
    .form-button-container {
      display: flex;
      gap: 10px;
      .form-button {
        width: 50%;
        height: 36px;
      }
    }
  }
  .visit-calendar-link {
    margin-left: 4px;
  }
`;

const LockedTransactionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  height: 68px;
  .locked-txs-title {
    font-size: 14px;
    font-weight: 700;
    margin: 0;
  }
`;

const IconComponentContainer = styled(IconTextContainer)`
  svg {
    path {
      fill: ${transparentWhite};
    }
  }
`;

const ShortSpan = styled.span`
  text-overflow: ellipsis;
  width: 45px;
  overflow: hidden;
`;

const MediumSpan = styled.span`
  text-overflow: ellipsis;
  max-width: 120px;
  overflow: hidden;
`;

const ZeroStateSpan = styled.span`
  color: #7d7d7d;
`;

type TokenWrapperProps = {
  indx: number;
};
const TokenWrapper = styled.div((props: TokenWrapperProps) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  rowGap: 16,
  paddingTop: props.indx === 0 ? 0 : 16,
}));
